import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { style: {"position":"relative","width":"100%","aspect-ratio":"16/9"} }

import { ref, reactive, onMounted, onUnmounted, watch, computed } from 'vue'
import Solar from '@/assets/datav/station/solar.svg'
import SolarS from '@/assets/datav/station/solar_s.svg'
// import Grid from '@/assets/datav/station/grid.svg'
import Grid from '@/assets/datav/station/grid.png'
import GridS from '@/assets/datav/station/grid_s.svg'
import Load from '@/assets/datav/station/load.svg'
import LoadS from '@/assets/datav/station/load_s.svg'
import Battery from '@/assets/datav/station/battery.svg'
import BatteryS from '@/assets/datav/station/battery_s.svg'
import Home from '@/assets/datav/station/home.svg'
import { EssEnergyFlow } from '@/types/types'
// Canvas reference

export default /*#__PURE__*/_defineComponent({
  props: {
    info: null,
    textColor: null
  },
  setup(__props: any) {

const props = __props as {
  info?: Partial<EssEnergyFlow>
  textColor?: string
}

const canvasRef = ref<HTMLCanvasElement | null>(null)
const canvasRefPic = ref<HTMLCanvasElement | null>(null)
const canvasRefText = ref<HTMLCanvasElement | null>(null)
let ctx: CanvasRenderingContext2D | null = null
let animationFrameId: number
const isCanvasReady = ref(false)



const nodeStatus = ref({
  pvDirection: false,
  gridDirection: false,
  loadDirection: false,
  batteryDirection: false,
  batteryDirectionReversal: false,
  gridDirectionReversal: false,
})

// Node configuration
const nodes = computed(() => {
  return [
    {
      id: 'pv',
      name: '光伏',
      x: 0.2,
      y: 0.2,
      icon: '☀️',
      color: '#092066',
      image: nodeStatus.value.pvDirection ? Solar : SolarS,
      text: [(props?.info?.PvPower || 0) + 'kW', (props?.info?.pvCapacity || 0) + ' kWp'],
    },
    {
      id: 'grid',
      name: '电网',
      x: 0.8,
      y: 0.2,
      icon: '⚡',
      color: '#092066',
      image:
        nodeStatus.value.gridDirection || nodeStatus.value.gridDirectionReversal ? Grid : GridS,
      text: [(props?.info?.gridPower ?? 0) + 'kW'],
    },
    {
      id: 'load',
      name: '负载',
      x: 0.2,
      y: 0.8,
      icon: '🏭',
      color: '#092066',
      image: nodeStatus.value.loadDirection ? Load : LoadS,
      text: [(props?.info?.loadPower ?? 0) + 'kW'],
    },
    {
      id: 'battery',
      name: '电池',
      x: 0.8,
      y: 0.8,
      icon: '🔋',
      color: '#092066',
      image:
        nodeStatus.value.batteryDirection || nodeStatus.value.batteryDirectionReversal
          ? Battery
          : BatteryS,
      text: [
        'SOC:' + (props?.info?.batterySOC ?? 0) + '%',
        (props?.info?.batteryPower || 0) + 'kW',
      ],
    },
    {
      id: 'manager',
      name: '管理器',
      x: 0.5,
      y: 0.5,
      icon: '⚙️',
      color: '#092066',
      image: Home,
      text: [],
    },
  ]
})

// Energy flow configuration with updated paths for H-shape
const flowConfig = computed(() => {
  return {
    flows: [
      // Solar to Manager (Goes right, then down to manager's top-left)
      {
        id: 'solarToManager',
        name: '太阳能 → 管理器',
        fromNodeId: 'pv',
        toNodeId: 'manager',
        isActive: nodeStatus.value.pvDirection,
        color: '#092066',
        path: [
          { x: 0.45, y: 0.2 },
          { x: 0.45, y: 0.4 },
        ],
      },
      // Manager to Grid (Goes up, then right)
      {
        id: 'managerToGrid',
        name: '管理器 → 电网',
        fromNodeId: 'manager',
        toNodeId: 'grid',
        isActive: nodeStatus.value.gridDirection,
        color: '#092066',
        path: [
          { x: 0.55, y: 0.4 },
          { x: 0.55, y: 0.2 },
        ],
      },
      // Manager to Load (Goes left, then down)
      {
        id: 'managerToLoad',
        name: '管理器 → 负载',
        fromNodeId: 'manager',
        toNodeId: 'load',
        isActive: nodeStatus.value.loadDirection,
        color: '#092066',
        path: [
          { x: 0.45, y: 0.6 },
          { x: 0.45, y: 0.8 },
        ],
      },
      // Manager to Battery (Goes right, then down)
      {
        id: 'managerToBattery',
        name: '管理器 → 电池',
        fromNodeId: 'manager',
        toNodeId: 'battery',
        isActive: nodeStatus.value.batteryDirection,
        color: '#092066',
        path: [
          { x: 0.55, y: 0.6 },
          { x: 0.55, y: 0.8 },
        ],
      },

      // Battery to Manager (Reverse flow, goes up from battery, then left to manager's bottom-right)
      {
        id: 'batteryToManager',
        name: '电池 → 管理器',
        fromNodeId: 'battery',
        toNodeId: 'manager',
        isActive: nodeStatus.value.batteryDirectionReversal,
        color: '#092066',
        reverse: true,
        path: [
          { x: 0.55, y: 0.8 },
          { x: 0.55, y: 0.6 },
        ],
      },
      // Grid to Manager (Reverse flow, goes down from grid, then left to manager's top-right)
      {
        id: 'gridToManager',
        name: '电网 → 管理器',
        fromNodeId: 'grid',
        toNodeId: 'manager',
        isActive: nodeStatus.value.gridDirectionReversal,
        color: '#092066',
        reverse: true,
        path: [
          { x: 0.55, y: 0.2 },
          { x: 0.55, y: 0.4 },
        ],
      },
      // Load to Manager (Reverse flow, goes up from load, then right to manager's bottom-left)
      {
        id: 'loadToManager',
        name: '负载 → 管理器',
        fromNodeId: 'load',
        toNodeId: 'manager',
        isActive: false,
        color: '#092066',
        reverse: true,
        path: [
          { x: 0.45, y: 0.8 },
          { x: 0.45, y: 0.6 },
        ],
      },
    ],
  }
})

// Animation progress for each flow (0 to 1)
const flowAnimationProgress = reactive<Record<string, number>>({})
const flowAnimationSpeed = reactive<Record<string, number>>({})

// Initialize animation point positions and speeds
flowConfig.value.flows.forEach((flow) => {
  flowAnimationProgress[flow.id] = Math.random() // Start at random progress
  flowAnimationSpeed[flow.id] = 0.005 + Math.random() * 0.0008 // Random speed
})

/**
 * Draws a node (unchanged from previous version)
 * @param {CanvasRenderingContext2D} ctx Canvas 2D context
 * @param {object} node Node data
 * @param {number} canvasWidth Canvas width
 * @param {number} canvasHeight Canvas height
 */
const drawNode = (
  ctx: CanvasRenderingContext2D,
  node,
  canvasWidth: number,
  canvasHeight: number,
) => {
  const nodeSize = Math.min(canvasWidth, canvasHeight) * 0.15 // Node size, responsive
  const nodeRadius = 15 // Corner radius
  const x = node.x * canvasWidth
  const y = node.y * canvasHeight

  ctx.save()
  ctx.translate(x, y)

  // let image = new Image()

  // image.onload = () => {
  //   ctx.drawImage(image, 0, 0)
  // }
  // image.src = node.image
  // console.log(image)

  // Draw node background (rounded rectangle)
  // ctx.beginPath()
  // ctx.moveTo(-nodeSize / 2 + nodeRadius, -nodeSize / 2)
  // ctx.lineTo(nodeSize / 2 - nodeRadius, -nodeSize / 2)
  // ctx.arcTo(nodeSize / 2, -nodeSize / 2, nodeSize / 2, -nodeSize / 2 + nodeRadius, nodeRadius)
  // ctx.lineTo(nodeSize / 2, nodeSize / 2 - nodeRadius)
  // ctx.arcTo(nodeSize / 2, nodeSize / 2, nodeSize / 2 - nodeRadius, nodeSize / 2, nodeRadius)
  // ctx.lineTo(-nodeSize / 2 + nodeRadius, nodeSize / 2)
  // ctx.arcTo(-nodeSize / 2, nodeSize / 2, -nodeSize / 2, nodeSize / 2 - nodeRadius, nodeRadius)
  // ctx.lineTo(-nodeSize / 2, -nodeSize / 2 + nodeRadius)
  // ctx.arcTo(-nodeSize / 2, -nodeSize / 2, -nodeSize / 2 + nodeRadius, -nodeSize / 2, nodeRadius)
  // ctx.closePath()

  // ctx.fillStyle = node.color
  // ctx.shadowColor = 'rgba(0, 0, 0, 0.2)'
  // ctx.shadowBlur = 10
  // ctx.shadowOffsetX = 3
  // ctx.shadowOffsetY = 3
  // ctx.fill()

  // // Draw icon
  // ctx.font = `${nodeSize * 0.4}px Arial` // Font size responsive
  // ctx.textAlign = 'center'
  // ctx.textBaseline = 'middle'
  // ctx.shadowBlur = 0 // Turn off icon shadow
  // ctx.fillText(node.icon, 0, -nodeSize * 0.15) // Icon slightly offset upwards

  // // Draw name
  // ctx.font = `${nodeSize * 0.15}px 'Inter', sans-serif` // Font size responsive
  // ctx.fillStyle = '#333'
  // ctx.shadowBlur = 0 // Turn off name shadow
  // ctx.fillText(node.name, 0, nodeSize * 0.3) // Name below icon

  ctx.restore()
}

/**
 * Calculates a point along a path at a given progress (pixel coordinates)
 * @param {number} progress Current progress (0-1)
 * @param {Array<object>} points Array of points defining the path (Canvas pixel coordinates)
 * @returns {object | null} {x, y} coordinates of the point, or null if path is invalid
 */
const getPointAlongPathPixels = (progress: number, points: []) => {
  if (!points || points.length < 1) {
    return null
  }
  if (points.length === 1) {
    return points[0]
  }

  let totalLength = 0
  const segmentLengths = []
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]
    const segLen = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
    segmentLengths.push(segLen)
    totalLength += segLen
  }

  let currentLength = progress * totalLength
  let accumulatedLength = 0

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]
    const segmentLength = segmentLengths[i]

    if (currentLength <= accumulatedLength + segmentLength) {
      const segmentProgress = (currentLength - accumulatedLength) / segmentLength
      const x = p1.x + (p2.x - p1.x) * segmentProgress
      const y = p1.y + (p2.y - p1.y) * segmentProgress
      return { x, y }
    }
    accumulatedLength += segmentLength
  }
  // If progress is 1, return the last point
  return points[points.length - 1]
}

/**
 * Draws connector lines and arrows
 * @param {CanvasRenderingContext2D} ctx Canvas 2D context
 * @param {Array<object>} fullPathPoints Array of all points (start, intermediates, end) in pixel coordinates
 * @param {string} color Line color
 * @param {number} arrowSize Arrow size
 */
const drawConnectorAndArrow = (
  ctx: CanvasRenderingContext2D,
  fullPathPoints: [],
  color: string,
  arrowSize: number,
) => {
  if (!fullPathPoints || fullPathPoints.length < 2) return

  ctx.beginPath()
  ctx.moveTo(fullPathPoints[0].x, fullPathPoints[0].y)

  for (let i = 1; i < fullPathPoints.length; i++) {
    ctx.lineTo(fullPathPoints[i].x, fullPathPoints[i].y)
  }

  ctx.strokeStyle = color
  ctx.lineWidth = 3
  ctx.stroke()

  // Draw arrow. The arrow direction should be based on the last segment of the path.
  const lastPoint = fullPathPoints[fullPathPoints.length - 1]
  const secondLastPoint = fullPathPoints[fullPathPoints.length - 2]
  const arrowAngle = Math.atan2(lastPoint.y - secondLastPoint.y, lastPoint.x - secondLastPoint.x)

  ctx.save()
  ctx.translate(lastPoint.x, lastPoint.y)
  ctx.rotate(arrowAngle)
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(-arrowSize, -arrowSize / 2)
  ctx.lineTo(-arrowSize, arrowSize / 2)
  ctx.closePath()
  ctx.fillStyle = color
  ctx.fill()
  ctx.restore()
}

/**
 * Draws the flowing element as a bending solid block with round caps, using a gradient.
 * This function aims to replicate the smooth bending effect seen in the reference image.
 * @param {CanvasRenderingContext2D} ctx Canvas 2D context
 * @param {object} flow Flow data
 * @param {number} canvasWidth Canvas width
 * @param {number} canvasHeight Canvas height
 * @param {string[]} gradientColors An array containing two color strings for the gradient [startColor, endColor].
 * Defaults to [flow.color, a transparent version of flow.color] if not provided.
 */
const drawFlowElement = (
  ctx: CanvasRenderingContext2D,
  flow: any,
  canvasWidth: number,
  canvasHeight: number,
  gradientColors = ['#092066', '#33d6ff'],
) => {
  const fromNode = nodes.value.find((n) => n.id === flow.fromNodeId)
  const toNode = nodes.value.find((n) => n.id === flow.toNodeId)

  if (!fromNode || !toNode) return

  // Construct the full path in canvas pixel coordinates
  const effectivePathPoints = [{ x: fromNode.x * canvasWidth, y: fromNode.y * canvasHeight }]
  if (flow.path && flow.path.length > 0) {
    flow.path.forEach((p) =>
      effectivePathPoints.push({
        x: p.x * canvasWidth,
        y: p.y * canvasHeight,
      }),
    )
  }
  effectivePathPoints.push({
    x: toNode.x * canvasWidth,
    y: toNode.y * canvasHeight,
  })

  const flowElementLengthPixels = 70 // Length of the moving block in pixels
  const elementWidth = 4 // Width of the moving block

  let totalPathLength = 0
  const segmentLengths = []
  for (let i = 0; i < effectivePathPoints.length - 1; i++) {
    const p1 = effectivePathPoints[i]
    const p2 = effectivePathPoints[i + 1]
    const segLen = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
    segmentLengths.push(segLen)
    totalPathLength += segLen
  }

  // Calculate the length of the flowing element as a ratio of the total path length
  const flowElementLengthProgress = flowElementLengthPixels / totalPathLength

  let headProgress = flowAnimationProgress[flow.id]
  let tailProgress = headProgress - flowElementLengthProgress

  // Handle wrap-around for smooth continuous animation
  let segmentsToDraw = []
  if (tailProgress < 0) {
    segmentsToDraw.push({ start: tailProgress + 1, end: 1 })
    segmentsToDraw.push({ start: 0, end: headProgress })
  } else {
    segmentsToDraw.push({ start: tailProgress, end: headProgress })
  }

  // Helper function to get a point along the path
  const getPointAlongPathPixels = (progress, pathPoints) => {
    if (progress < 0 || progress > 1) return null
    let currentLength = 0
    for (let i = 0; i < pathPoints.length - 1; i++) {
      const p1 = pathPoints[i]
      const p2 = pathPoints[i + 1]
      const segmentLength = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))

      if (currentLength + segmentLength >= progress * totalPathLength) {
        const segmentProgress = (progress * totalPathLength - currentLength) / segmentLength
        return {
          x: p1.x + (p2.x - p1.x) * segmentProgress,
          y: p1.y + (p2.y - p1.y) * segmentProgress,
        }
      }
      currentLength += segmentLength
    }
    return pathPoints[pathPoints.length - 1]
  }

  // --- Gradient Definition ---
  // Use the passed gradientColors, or fallback to default if not provided
  const [startColor, endColor] = gradientColors || [
    flow.color, // Default start color
    `rgba(${parseInt(flow.color.substring(1, 3), 16)}, ${parseInt(
      flow.color.substring(3, 5),
      16,
    )}, ${parseInt(flow.color.substring(5, 7), 16)}, 0.5)`, // Default end color (semi-transparent version)
  ]

  // Draw each segment of the flowing element
  segmentsToDraw.forEach((segment) => {
    const segmentActualLength = (segment.end - segment.start) * totalPathLength
    const numSamples = Math.max(2, Math.ceil(segmentActualLength / 5))

    const sampledPoints = []
    for (let j = 0; j <= numSamples; j++) {
      const p = segment.start + (segment.end - segment.start) * (j / numSamples)
      const point = getPointAlongPathPixels(p, effectivePathPoints)
      if (point) {
        sampledPoints.push(point)
      }
    }

    if (sampledPoints.length > 1) {
      // Create a linear gradient along the length of the current segment
      const gradient = ctx.createLinearGradient(
        sampledPoints[0].x,
        sampledPoints[0].y,
        sampledPoints[sampledPoints.length - 1].x,
        sampledPoints[sampledPoints.length - 1].y,
      )
      gradient.addColorStop(0, startColor) // Use the passed start color
      gradient.addColorStop(1, endColor) // Use the passed end color

      ctx.beginPath()
      ctx.moveTo(sampledPoints[0].x, sampledPoints[0].y)
      for (let k = 1; k < sampledPoints.length; k++) {
        ctx.lineTo(sampledPoints[k].x, sampledPoints[k].y)
      }

      ctx.strokeStyle = gradient // Assign the created gradient to strokeStyle
      ctx.lineWidth = elementWidth
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.stroke()

      // Optional: Add a slight "glow" or outline
      ctx.lineWidth = elementWidth + 2
      ctx.strokeStyle = `rgba(${parseInt(flow.color.substring(1, 3), 16)}, ${parseInt(
        flow.color.substring(3, 5),
        16,
      )}, ${parseInt(flow.color.substring(5, 7), 16)}, 0.2)` // Still using flow.color for base glow
      ctx.stroke()
    }
  })
}
/**
 * Animation loop
 */
const animate = () => {
  if (!ctx || !canvasRef.value) return

  const canvas = canvasRef.value
  const dpi = window.devicePixelRatio || 1
  const rect = canvas.getBoundingClientRect()
  canvas.width = rect.width * dpi
  canvas.height = rect.height * dpi
  ctx.scale(dpi, dpi)
  const canvasWidth = rect.width
  const canvasHeight = rect.height

  ctx.clearRect(0, 0, canvasWidth, canvasHeight) // Clear the canvas

  // Draw all nodes
  // nodes.value.forEach((node) => {
  //   drawNode(ctx, node, canvasWidth, canvasHeight)
  // })

  // Draw connector lines for all flows
  flowConfig.value.flows.forEach((flow) => {
    const fromNode = nodes.value.find((n) => n.id === flow.fromNodeId)
    const toNode = nodes.value.find((n) => n.id === flow.toNodeId)
    if (fromNode && toNode) {
      const nodeSize = Math.min(canvasWidth, canvasHeight) * 0.15
      const startX = fromNode.x * canvasWidth
      const startY = fromNode.y * canvasHeight
      const endX = toNode.x * canvasWidth
      const endY = toNode.y * canvasHeight

      // Construct the full path in canvas coordinates for line drawing
      const linePoints = [{ x: startX, y: startY }]
      if (flow.path && flow.path.length > 0) {
        flow.path.forEach((p) =>
          linePoints.push({
            x: p.x * canvasWidth,
            y: p.y * canvasHeight,
          }),
        )
      }
      linePoints.push({ x: endX, y: endY })

      // Calculate adjusted start and end points to draw lines from node edges
      let adjustedStartX = startX
      let adjustedStartY = startY
      let adjustedEndX = endX
      let adjustedEndY = endY

      if (linePoints.length > 1) {
        // Determine direction of the first segment
        const firstSegmentDirectionX = linePoints[1].x - linePoints[0].x
        const firstSegmentDirectionY = linePoints[1].y - linePoints[0].y
        const firstSegmentLength = Math.sqrt(
          firstSegmentDirectionX * firstSegmentDirectionX +
            firstSegmentDirectionY * firstSegmentDirectionY,
        )
        if (firstSegmentLength > 0) {
          adjustedStartX = startX + (firstSegmentDirectionX / firstSegmentLength) * (nodeSize / 2)
          adjustedStartY = startY + (firstSegmentDirectionY / firstSegmentLength) * (nodeSize / 2)
        }

        // Determine direction of the last segment
        const lastPointIndex = linePoints.length - 1
        const secondToLastPoint = linePoints[lastPointIndex - 1]
        const lastSegmentDirectionX = linePoints[lastPointIndex].x - secondToLastPoint.x
        const lastSegmentDirectionY = linePoints[lastPointIndex].y - secondToLastPoint.y
        const lastSegmentLength = Math.sqrt(
          lastSegmentDirectionX * lastSegmentDirectionX +
            lastSegmentDirectionY * lastSegmentDirectionY,
        )
        if (lastSegmentLength > 0) {
          adjustedEndX = endX - (lastSegmentDirectionX / lastSegmentLength) * (nodeSize / 2)
          adjustedEndY = endY - (lastSegmentDirectionY / lastSegmentLength) * (nodeSize / 2)
        }
      }

      // Create a new path array for drawing the line itself, incorporating adjusted start/end
      const pathForDrawing = []
      pathForDrawing.push({ x: adjustedStartX, y: adjustedStartY })
      if (flow.path && flow.path.length > 0) {
        flow.path.forEach((p) =>
          pathForDrawing.push({
            x: p.x * canvasWidth,
            y: p.y * canvasHeight,
          }),
        )
      }
      pathForDrawing.push({ x: adjustedEndX, y: adjustedEndY })

      drawConnectorAndArrow(
        ctx,
        pathForDrawing, // Pass the full adjusted path for drawing the connector line
        flow.color,
        0, // Arrow size
      )
    }
  })

  // Update and draw active animated flow elements
  flowConfig.value.flows.forEach((flow) => {
    if (flow.isActive) {
      flowAnimationProgress[flow.id] = flowAnimationProgress[flow.id] + flowAnimationSpeed[flow.id]
      // Reset progress when it wraps around
      if (flowAnimationProgress[flow.id] >= 1) {
        flowAnimationProgress[flow.id] = 0
      }
      drawFlowElement(ctx, flow, canvasWidth, canvasHeight)
    }
  })

  animationFrameId = requestAnimationFrame(animate)
}

// Listen for window resize to re-render Canvas
const handleResize = () => {
  if (ctx && canvasRef.value) {
    const dpi = window.devicePixelRatio || 1
    const rect = canvasRef.value.getBoundingClientRect()
    canvasRef.value.width = rect.width * dpi
    canvasRef.value.height = rect.height * dpi
    ctx.scale(dpi, dpi)
  }
  const ctxs = canvasRefPic.value?.getContext('2d')
  if (ctxs && canvasRefPic.value) {
    const dpi = window.devicePixelRatio || 1
    const rect = canvasRefPic.value.getBoundingClientRect()
    canvasRefPic.value.width = rect.width * dpi
    canvasRefPic.value.height = rect.height * dpi
    if (ctxs) {
      ctxs.scale(dpi, dpi)
      drawImage(ctxs)
    }
  }
}

const drawImage = (ctxs: CanvasRenderingContext2D) => {
  const canvas = canvasRefPic.value as HTMLCanvasElement
  const rect = canvas.getBoundingClientRect()
  const nodeSize = Math.min(rect.width, rect.height) * 0.3
  const dpr = window.devicePixelRatio || 1
  canvas.width = rect.width * dpr
  canvas.height = rect.height * dpr
  canvas.style.width = rect.width + 'px'
  canvas.style.height = rect.height + 'px'
  ctxs.setTransform(1, 0, 0, 1, 0, 0) // 重置变换
  ctxs.scale(dpr, dpr) // 按设备像素比缩放
  const loadImages = async () => {
    const promises = nodes.value.map(
      (node) =>
        new Promise<HTMLImageElement>((resolve) => {
          const img = new Image()
          img.onload = () => resolve(img)
          img.src = node.image
        }),
    )

    const images = await Promise.all(promises)
    ctxs.clearRect(0, 0, canvas.width, canvas.height)

    images.forEach((img, i) => {
      const node = nodes.value[i]
      const x = node.x * rect.width - nodeSize / 2
      const y = node.y * rect.height - nodeSize / 2
      ctxs.drawImage(img, x, y, nodeSize, nodeSize)
    })
  }
  console.log(nodes)
  console.log(props.info)
  loadImages()
}
const drawText = (ctxs: CanvasRenderingContext2D) => {
  const canvas = canvasRefText.value as HTMLCanvasElement
  if (!canvas) return
  const rect = canvas.getBoundingClientRect()
  const nodeSize = Math.min(rect.width, rect.height) * 0.3
  const dpr = window.devicePixelRatio || 1
  canvas.width = rect.width * dpr
  canvas.height = rect.height * dpr
  canvas.style.width = rect.width + 'px'
  canvas.style.height = rect.height + 'px'
  ctxs.setTransform(1, 0, 0, 1, 0, 0)
  ctxs.scale(dpr, dpr)
  ctxs.clearRect(0, 0, canvas.width, canvas.height)

  nodes.value.forEach((node) => {
    if (!node.text || node.text.length === 0) return
    let x, y
    if (node.x > 0.5) {
      x = node.x * rect.width * 0.93
      y = node.y * rect.height * 0.9
    } else {
      x = node.x * rect.width * 1.2
      y = node.y * rect.height * 0.9
    }

    ctxs.font = `${Math.round(nodeSize * 0.13)}px "Inter", Arial, sans-serif`
    ctxs.fillStyle = props.textColor ? props.textColor : '#fff'
    ctxs.textBaseline = 'middle'

    // 判断左右侧：左侧节点（x < 0.5）文字放右侧，右侧节点（x >= 0.5）文字放左侧
    let alignLeft = node.x <= 0.5
    ctxs.textAlign = alignLeft ? 'right' : 'left'
    // 文字起始x坐标
    const textX = alignLeft
      ? x - nodeSize / 2 - 8 // 左侧，图片左边
      : x + nodeSize / 2 + 8 // 右侧，图片右边

    // 多行垂直居中分布
    const totalHeight = node.text.length * nodeSize * 0.25
    const startY = y - totalHeight / 2 + nodeSize * 0.075

    ;[node.name, ...node.text].forEach((line, idx) => {
      ctxs.fillText(line.toString(), textX, startY + idx * (nodeSize * 0.15))
    })
  })
}

onMounted(() => {
  if (canvasRefText.value) {
    const ctxs = canvasRefText.value.getContext('2d')
    if (ctxs) {
      drawText(ctxs)
    }
  }
  if (canvasRef.value) {
    ctx = canvasRef.value.getContext('2d')
    if (ctx) {
      isCanvasReady.value = true
      // Initial draw
      animate()
      window.addEventListener('resize', handleResize)
    }
  }
})

onUnmounted(() => {
  cancelAnimationFrame(animationFrameId)
  window.removeEventListener('resize', handleResize)
})
// const isChangeImageStatus = (newInfo,oldInfo)=>{
//   const {
// pvDirection
// gridDirection
// loadDirection
// batteryDirection
// batteryDirectionReversal
// gridDirectionReversal
// } = newInfo

// }
watch(
  () => props.info,
  (newInfo, oldInfo) => {
    nodeStatus.value = {
      pvDirection: props?.info?.pvDirection == 1 ? true : false,
      gridDirection: props?.info?.gridDirection == 1 ? true : false,
      loadDirection: props?.info?.loadDirection == 1 ? true : false,
      batteryDirection: props?.info?.batteryDirection == -1 ? true : false,
      batteryDirectionReversal: props?.info?.batteryDirection == 1 ? true : false,
      gridDirectionReversal: props?.info?.gridDirection == -1 ? true : false,
    }
    if (canvasRef.value) {
      ctx = canvasRef.value.getContext('2d')
      cancelAnimationFrame(animationFrameId)
      if (ctx) {
        isCanvasReady.value = true
        // Initial draw
        animate()
      }
    }
    // 只有图片资源变动时才重绘图片，避免每次数据变动都重绘导致闪烁
    if (
      oldInfo?.pvDirection !== newInfo?.pvDirection ||
      oldInfo?.gridDirection !== newInfo?.gridDirection ||
      oldInfo?.loadDirection !== newInfo?.loadDirection ||
      oldInfo?.batteryDirection !== newInfo?.batteryDirection
    ) {
      if (canvasRefPic.value) {
        const ctxs = canvasRefPic.value.getContext('2d')
        if (ctxs) {
          drawImage(ctxs)
        }
      }
    }
    if (canvasRefText.value) {
      const ctxs = canvasRefText.value.getContext('2d')
      if (ctxs) {
        drawText(ctxs)
      }
    }
  },
  { immediate: true, deep: true, flush: 'post' },
)

// Return data and methods exposed to the template from the setup function

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("canvas", {
      ref_key: "canvasRef",
      ref: canvasRef,
      style: {"width":"90%","aspect-ratio":"16/9","margin":"0 auto"},
      class: "canvas-dom"
    }, null, 512),
    _createElementVNode("canvas", {
      ref_key: "canvasRefPic",
      ref: canvasRefPic,
      style: {"width":"90%","aspect-ratio":"16/9","margin":"0 auto"},
      class: "canvas-dom"
    }, null, 512),
    _createElementVNode("canvas", {
      ref_key: "canvasRefText",
      ref: canvasRefText,
      style: {"width":"100%","aspect-ratio":"16/9"},
      class: "canvas-dom"
    }, null, 512)
  ]))
}
}

})